// src/utils/firestoreUtils.js
import dayjs from "@/plugins/dayjs";
import { normalizeDateRange, formatTimeFromMs } from "@/utils/timeFormatUtils";

import {
    Timestamp,
    collection,
    query,
    where,
    getDocs,
    or,
    and,
} from "firebase/firestore";

/**
 * Return a short suffix for display next to a period label.
 *
 * @param {string} key - One of the period keys
 * @returns {string} The short suffix (e.g., "(T)", "(TW)").
 * */
export function periodSuffix(key) {
    const map = {
        today: "(T)",
        yesterday: "(Y)",
        thisWeek: "(TW)",
        lastWeek: "(LW)",
        thisMonth: "(TM)",
        lastMonth: "(LM)",
        custom: "(C)",
    };
    return map[key] || "";
}

/**
 * A list of all supported reporting periods, for populating menus or selectors.
 *
 * @type {Array.<{key: string, label: string}>}
 * @constant
 * @property {string} key - The internal period identifier (e.g. "today").
 * @property {string} label - The human-readable label (e.g. "Today").
 */
export const PERIODS = [
    { key: "today", label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "thisWeek", label: "This Week" },
    { key: "lastWeek", label: "Last Week" },
    { key: "thisMonth", label: "This Month" },
    { key: "lastMonth", label: "Last Month" },
    { key: "custom", label: "Custom" },
];

/**
 * Get the start and end range for a given reporting period.
 * Always returns UTC epoch millisecond numbers (for RTDB compatibility).
 * Can be converted to Firestore Timestamps when needed using Timestamp.fromMillis().
 *
 * @param {string} key - One of:
 *   "today", "yesterday", "thisWeek", "lastWeek", "thisMonth", "lastMonth", "custom"
 * @param {Array<Date>|null} customDateRange - Custom date range array (for "custom" period)
 * @returns {{ startDate: number, endDate: number }} UTC epoch milliseconds.
 * @throws {Error} If the provided key is not recognized or custom date range is invalid.
 */
export function getDateRangeForPeriod(key, customDateRange = null) {
    const now = dayjs();

    // Handle custom date range
    if (key === "custom") {
        if (!customDateRange || !Array.isArray(customDateRange) || customDateRange.length === 0) {
            throw new Error("Custom date range must be a non-empty array");
        }
        const normalized = normalizeDateRange(
            customDateRange[0],
            customDateRange[customDateRange.length - 1]
        );
        if (!normalized) {
            throw new Error("Invalid custom date range");
        }
        return {
            startDate: normalized.start,
            endDate: normalized.end,
        };
    }

    // Handle preset periods
    switch (key) {
        case "today": {
            const utcStart = now.startOf("day").utc().valueOf();
            const utcEnd = now.endOf("day").utc().valueOf();
            return {
                startDate: utcStart,
                endDate: utcEnd,
            };
        }

        case "yesterday": {
            const utcStart = now
                .subtract(1, "day")
                .startOf("day")
                .utc()
                .valueOf();
            const utcEnd = now.subtract(1, "day").endOf("day").utc().valueOf();
            return {
                startDate: utcStart,
                endDate: utcEnd,
            };
        }

        case "thisWeek": {
            const utcStart = now.startOf("isoWeek").utc().valueOf();
            const utcEnd = now.endOf("isoWeek").utc().valueOf();
            return {
                startDate: utcStart,
                endDate: utcEnd,
            };
        }

        case "lastWeek": {
            const lastWeek = now.subtract(1, "week");
            const utcStart = lastWeek.startOf("isoWeek").utc().valueOf();
            const utcEnd = lastWeek.endOf("isoWeek").utc().valueOf();
            return {
                startDate: utcStart,
                endDate: utcEnd,
            };
        }

        case "thisMonth": {
            const utcStart = now.startOf("month").utc().valueOf();
            const utcEnd = now.endOf("month").utc().valueOf();
            return {
                startDate: utcStart,
                endDate: utcEnd,
            };
        }

        case "lastMonth": {
            const lastMonth = now.subtract(1, "month");
            const utcStart = lastMonth.startOf("month").utc().valueOf();
            const utcEnd = lastMonth.endOf("month").utc().valueOf();
            return {
                startDate: utcStart,
                endDate: utcEnd,
            };
        }

        default:
            throw new Error(`Unknown period key: ${key}`);
    }
}

/**
 * Determines if Firestore fetch is needed based on period and date range.
 * RTDB only contains the last 45 days of data, so we need Firestore for:
 * 1. "lastMonth" period when we're 14+ days into the current month
 * 2. Custom periods that extend beyond 45 days from today
 *
 * @param {string} periodKey - The period key ("today", "yesterday", "lastMonth", "custom", etc.)
 * @param {Array<Date>|null} customDateRange - Custom date range array (for "custom" period)
 * @returns {boolean} True if Firestore fetch is needed, false otherwise
 */
export function shouldFetchFromFirestore(periodKey, customDateRange = null) {
    const now = dayjs();
    const fortyFiveDaysAgo = now.subtract(45, "days").startOf("day").utc().valueOf();
    const daysIntoMonth = now.date(); // 1-31

    // Check if we're 14+ days into current month (for lastMonth, we'd need data older than 45 days)
    if (periodKey === "lastMonth" && daysIntoMonth >= 14) {
        return true;
    }

    // Check if custom period extends beyond 45 days
    if (periodKey === "custom" && customDateRange && Array.isArray(customDateRange)) {
        const customStart = dayjs(customDateRange[0]).startOf("day").utc().valueOf();
        if (customStart < fortyFiveDaysAgo) {
            return true;
        }
    }

    return false;
}

/**
 * Calculates the date range that needs to be fetched from Firestore.
 * Returns the portion of the requested range that is older than 45 days.
 *
 * @param {{startDate: number|Date, endDate: number|Date}} requestedRange - The full requested date range
 * @returns {{startDate: number, endDate: number}|null} The range to fetch from Firestore, or null if not needed
 */
export function getFirestoreDateRange(requestedRange) {
    if (!requestedRange || !requestedRange.startDate || !requestedRange.endDate) {
        return null;
    }

    const now = dayjs();
    const fortyFiveDaysAgo = now.subtract(45, "days").startOf("day").utc().valueOf();

    // Normalize requested range to UTC milliseconds
    const normalized = normalizeDateRange(
        requestedRange.startDate,
        requestedRange.endDate
    );
    if (!normalized) return null;
    
    const requestedStart = normalized.start;
    const requestedEnd = normalized.end;

    // If requested range doesn't extend beyond 45 days, no Firestore fetch needed
    if (requestedStart >= fortyFiveDaysAgo) {
        return null;
    }

    // Firestore range: from requested start to 45 days ago (or requested end, whichever is earlier)
    const firestoreEnd = Math.min(requestedEnd, fortyFiveDaysAgo - 1);

    return {
        startDate: requestedStart,
        endDate: firestoreEnd,
    };
}

/**
 * Maximum number of OR clauses allowed in a Firestore query.
 */
const MAX_OR_CLAUSES = 30;

/**
 * Split an array into smaller chunks to avoid exceeding Firestore's `or` clause limit.
 *
 * Firestore queries allow a maximum of 30 `or` conditions, so when querying many IDs,
 * this helper splits the list into batches of ≤30 IDs each. These batches are then queried separately
 * and combined afterward.
 *
 * @param {Array<string>} arr - The full array to split.
 * @param {number} size - The maximum number of items per chunk (usually 30).
 * @returns {Array<Array<string>>} An array of arrays, each no longer than `size`.
 *
 * @example
 * // Input: ['p1', 'p2', ..., 'p100'], size: 30
 * // Output: [['p1', ..., 'p30'], ['p31', ..., 'p60'], ['p61', ..., 'p90'], ['p91', ..., 'p100']]
 */
export function chunkArray(arr, size) {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
    }
    return chunks;
}

/**
 * Fetches and aggregates timeEntries by project, optionally scoped to one user and his assigned projects
 * and/or a list of project IDs.
 *
 * @param {Timestamp} start - The start of the date range (Firestore Timestamp).
 * @param {Timestamp} end - The end of the date range (Firestore Timestamp).
 * @param {Object<string, string>} projectNames - A map of projectId to projectName.
 * @param {string|null} userId - Optional userId to filter by.
 * @param {string[]} projectIds - Optional list of project IDs to filter.
 * @returns {Promise<Array<{
 *   projectId: string,
 *   projectName: string,
 *   entries: number,
 *   users: number,
 *   duration: string,
 *   hasTimeEntries: boolean
 * }>>} Aggregated project-level report data.
 */

export async function getReportData(
    firestore,
    start,
    end,
    projectNames = {},
    userId = null,
    projectIds = []
) {
    // Helper to build a single Query for a chunk of projectIds
    function makeQueryFor(projectIdChunk) {
        // build your unchanged base clauses
        const base = [
            where("deleted", "==", false),
            where("endTime", ">=", start),
            where("endTime", "<=", end),
        ];
        if (userId) {
            base.push(where("userId", "==", userId));
        }

        let finalFilter;
        if (projectIdChunk.length) {
            // build your OR-clause
            const orFilter = or(
                ...projectIdChunk.map((pid) => where("projectId", "==", pid))
            );
            // now AND everything together in one go
            finalFilter = and(...base, orFilter);
            // and pass only that one filter into query()
            return query(collection(firestore, "timeEntries"), finalFilter);
        } else {
            // no OR needed, just spread the primitive wheres
            return query(collection(firestore, "timeEntries"), ...base);
        }
    }

    // Decide: either 1 query (≤30 IDs or none), or chunk into many
    const queries =
        projectIds.length > MAX_OR_CLAUSES
            ? chunkArray(projectIds, MAX_OR_CLAUSES).map(makeQueryFor)
            : [makeQueryFor(projectIds)];

    // Fetch all snapshots in parallel
    const snaps = await Promise.all(queries.map((q) => getDocs(q)));

    // Aggregate across all docs
    // 1) seed byProj with *all* the IDs you passed in
    const byProj = projectIds.reduce((acc, pid) => {
        acc[pid] = {
            duration: 0,
            overtimeDuration: 0,
            entries: 0,
            users: new Set(),
        };
        return acc;
    }, {});

    // 2) now aggregate across all the docs you fetched
    snaps.forEach((snap) =>
        snap.forEach((doc) => {
            const {
                projectId,
                userId: uid,
                startTime,
                endTime,
                overtime,
            } = doc.data();

            if (projectId === "SYSTEM_LUNCH-BREAK") {
                return;
            }

            const dur = endTime.toMillis() - startTime.toMillis();
            if (!byProj[projectId]) {
                // in case a projectId wasn’t in your list, still catch it
                byProj[projectId] = {
                    duration: 0,
                    overtimeDuration: 0,
                    entries: 0,
                    users: new Set(),
                };
            }
            byProj[projectId].duration += dur;
            if (overtime === true) {
                byProj[projectId].overtimeDuration += dur;
            }
            byProj[projectId].entries += 1;
            byProj[projectId].users.add(uid);
        })
    );

    // 3) shape into your final array, marking missing‐entry projects as N/A
    return Object.entries(byProj)
        .filter(([pid]) => pid !== "SYSTEM_LUNCH-BREAK")
        .map(([pid, stats]) => {
            const hasAny = stats.entries > 0;

            const duration = hasAny ? formatTimeFromMs(stats.duration) : "N/A";
            const overtime = hasAny ? formatTimeFromMs(stats.overtimeDuration) : "N/A";

            return {
                projectId: pid,
                projectName: projectNames[pid] || pid,
                entries: stats.entries,
                users: stats.users.size,
                duration,
                overtime, 
                hasTimeEntries: hasAny,
            };
        });
}

/**
 * Fetches time entries for multiple users within [start, end].
 * Returns entries grouped by userId: { userId: [{ id, projectId, ... }] }.
 *
 * @param {Firestore} firestore
 * @param {Array<string>} userIds - Array of user IDs to fetch entries for
 * @param {Timestamp|number} start - Start timestamp (Timestamp or UTC milliseconds)
 * @param {Timestamp|number} end - End timestamp (Timestamp or UTC milliseconds)
 * @returns {Promise<Object>} Object mapping userId to array of time entries
 */
export async function getTimeEntriesForUsersInRange(
    firestore,
    userIds,
    start,
    end
) {
    if (!userIds || userIds.length === 0) {
        return {};
    }

    // Convert milliseconds to Timestamp if needed
    const startTimestamp = typeof start === "number" 
        ? Timestamp.fromMillis(start)
        : start;
    const endTimestamp = typeof end === "number"
        ? Timestamp.fromMillis(end)
        : end;

    // Firestore has a limit of 30 OR clauses, so we need to chunk userIds
    const userIdChunks = chunkArray(userIds, MAX_OR_CLAUSES);
    
    // Build queries for each chunk
    const queries = userIdChunks.map((chunk) => {
        const baseClauses = [
            where("deleted", "==", false),
            where("endTime", ">=", startTimestamp),
            where("endTime", "<=", endTimestamp),
        ];
        
        if (chunk.length === 1) {
            // Single user: no OR needed, just spread the primitive wheres
            baseClauses.push(where("userId", "==", chunk[0]));
            return query(collection(firestore, "timeEntries"), ...baseClauses);
        } else {
            // Multiple users: need OR filter, wrap everything in and()
            const orFilter = or(...chunk.map((uid) => where("userId", "==", uid)));
            const finalFilter = and(...baseClauses, orFilter);
            return query(collection(firestore, "timeEntries"), finalFilter);
        }
    });

    // Fetch all queries in parallel
    const snaps = await Promise.all(queries.map((q) => getDocs(q)));

    // Group entries by userId
    const entriesByUser = {};
    snaps.forEach((snap) => {
        snap.forEach((doc) => {
            const d = doc.data();
            const userId = d.userId;
            
            if (!entriesByUser[userId]) {
                entriesByUser[userId] = [];
            }
            
            entriesByUser[userId].push({
                id: doc.id,
                projectId: d.projectId,
                description: d.description || null,
                startTime: d.startTime.toMillis(),
                endTime: d.endTime.toMillis(),
                overtime: d.overtime === true,
            });
        });
    });

    return entriesByUser;
}
